import { Icon, Stack, Tag, TagLabel, TagLeftIcon, Text } from '@chakra-ui/react';
import Link from 'components/Link';
import { StatusTag } from 'components/StatusTag';
import { MultiSelectDropDown } from 'components/Table/MultiSelectDropDown';
import { get, isEqual, isNil } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BsExclamationCircleFill } from 'react-icons/bs';
import { FaShippingFast } from 'react-icons/fa';

import { TaskIcon } from '../../components/Icons';
import { DataTable } from '../../components/Table/DataTable';
import { SelectColumnFilter } from '../../components/Table/Filter';
import { useUserContext } from '../../context/UserContextProvider';
import { dateTimeTransformer, dateTimezoneTransformer } from '../../helpers/dateTransformer';
import { createDropdownPlainOptions, getPriorityMap } from '../../helpers/general';
import useTasksQuery from '../../hooks/useTasksQuery';
import useUsersQuery from '../../hooks/useUsersQuery';
import { ContentCard } from '../../layout/ContentCard';
import { PagePathMapping } from '../promotion/dashboard/Dashboard';

const MyTasksOverviewCard = (props: { autoFocus?: boolean }) => {
  const { autoFocus } = props;
  const { t } = useTranslation();
  const { statusCodes, user } = useUserContext();
  const tasksStatusCodes = get(statusCodes, 'task', {});
  const initialStatus = ['0', '200', '300'];
  const usersQuery = useUsersQuery();

  const PRIORITY_MAP = getPriorityMap(t);

  const enum TASK_TYPE {
    account_registration = 'tasks:data_table.types.account_registration',
    service_entry = 'tasks:data_table.types.service_entry',
    service_entry_qa = 'tasks:data_table.types.service_entry_qa',
    service_entry_deletion = 'tasks:data_table.types.service_entry_deletion',
    service_history_request = 'tasks:data_table.types.service_history_request',
    workshop_data_update = 'tasks:data_table.types.workshop_data_update',
    verification = 'tasks:data_table.types.verification',
    verification_step_1 = 'tasks:data_table.types.verification_step_1',
    verification_step_2 = 'tasks:data_table.types.verification_step_2',
    account_deletion = 'tasks:data_table.types.account_deletion',
    b2c_verification = 'tasks:data_table.types.b2c_verification',
    b2c_service_history_request = 'tasks:data_table.types.b2c_service_history_request',
  }
  const allTaskTypes = [
    {
      label: t('tasks:data_table.types.account_registration'),
      value: 'account_registration',
    },
    {
      label: t('tasks:data_table.types.service_entry'),
      value: 'service_entry',
    },
    {
      label: t('tasks:data_table.types.service_entry_qa'),
      value: 'service_entry_qa',
    },
    {
      label: t('tasks:data_table.types.service_entry_deletion'),
      value: 'service_entry_deletion',
    },
    {
      label: t('tasks:data_table.types.service_history_request'),
      value: 'service_history_request',
    },
    {
      label: t('tasks:data_table.types.workshop_data_update'),
      value: 'workshop_data_update',
    },
    {
      label: t('tasks:data_table.types.verification'),
      value: 'verification',
    },
    {
      label: t('tasks:data_table.types.verification_step_1'),
      value: 'verification_step_1',
    },
    {
      label: t('tasks:data_table.types.verification_step_2'),
      value: 'verification_step_2',
    },
    {
      label: t('tasks:data_table.types.account_deletion'),
      value: 'account_deletion',
    },
    {
      label: t('tasks:data_table.types.b2c_verification'),
      value: 'b2c_verification',
    },
    {
      label: t('tasks:data_table.types.b2c_service_history_request'),
      value: 'b2c_service_history_request',
    },
  ];

  const initialFilters = {
    searchString: '',
    itemsPerPage: 20,
    status: initialStatus || [],
    type: undefined,
    priority: [],
  };

  const localStorageData = window.localStorage.getItem('my_tasks_filters');
  // @ts-ignore
  const destringifiedData = JSON.parse(localStorageData) || {};
  useEffect(() => {
    if (!localStorageData) {
      window.localStorage.setItem('my_tasks_filters', JSON.stringify(initialFilters));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [searchString, setSearchString] = useState(destringifiedData?.searchString || initialFilters?.searchString);
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(destringifiedData?.itemsPerPage || initialFilters?.itemsPerPage);
  const [status, setStatus] = useState(initialFilters?.status);
  const [priority, setPriority] = useState<number[]>([]);
  const [type, setType] = useState(destringifiedData?.type || initialFilters?.type);
  const [filtersSelected, setFiltersSelected] = useState(false);
  const sort = 'created_at';
  const order = 'asc';
  const tasksQuery = useTasksQuery({
    searchString,
    page,
    itemsPerPage,
    status,
    type,
    sort,
    order,
    assignee: user.id,
    priority: priority.length > 0 ? priority : null,
  });
  // @ts-ignore
  const currentPage = tasksQuery.data?.current_page - 1;
  const initialLoad = searchString === '' && itemsPerPage === 20 && page === 1 && isEqual(initialStatus, status) && isNil(type);

  useEffect(() => {
    setPage(1);
  }, [searchString, itemsPerPage, type, priority]);

  useEffect(() => {
    setFiltersSelected(!isNil(priority) || !isNil(type));

    window.localStorage.setItem(
      'tasks_filters',
      JSON.stringify({
        searchString: searchString,
        status: status,
        itemsPerPage: itemsPerPage,
        priority: priority.length > 0 ? priority : null,
      }),
    );
  }, [searchString, status, type, itemsPerPage, priority]);

  const columns = useMemo(
    () => [
      {
        accessor: 'highlighted',
        // @ts-ignore
        Cell: (cellProps: any) => {
          const { row } = cellProps;
          return row.original?.highlighted ? <Icon as={BsExclamationCircleFill} boxSize={6} mr={-50} color="orange.400" /> : null;
        },
      },
      {
        accessor: 'fastlane',
        // @ts-ignore
        Cell: ({ row }) => {
          return row?.original?.priority === 100 ? <Icon as={FaShippingFast} boxSize={8} mr={-50} color="orange.400" /> : null;
        },
      },
      {
        Header: 'Status',
        accessor: 'status',
        // @ts-ignore
        Cell: (cellProps) => {
          // @ts-ignore
          const { row } = cellProps;

          const TypeMappingToStatus = {
            'App\\Models\\ServiceRecord': 'service_record',
            'App\\Models\\ServiceHistoryRequest': 'service_history_request',
            'App\\Models\\WorkshopUpdateRequest': 'workshop_update_request',
            'App\\Models\\WorkshopVerification': 'workshop_verification_request',
            'App\\Models\\Account': 'account',
            'App\\Models\\B2C\\B2CVerification': 'b2c_verification_request',
            'App\\Models\\B2C\\B2CServiceHistoryRequest': 'b2c_service_history_request',
          };

          return (
            <StatusTag
              // @ts-ignore
              resourceKey={TypeMappingToStatus[row.original?.subject_type]}
              currentStatus={row?.original?.subject?.status ?? 0}
              currentStatusReason={row.original?.subject?.status_reason}
              isCell={true}
            />
          );
        },
      },
      {
        Header: t('forms:open_task.label'),
        accessor: 'last_checked_at',
        // @ts-ignore
        Cell: ({ value }) => {
          return (
            <Tag variant={'solid'} size={'md'} padding={'6px'} colorScheme={isNil(value) ? 'red' : 'green'}>
              {isNil(value) ? t('tasks:last_checked_at.not_checked') : dateTimezoneTransformer(value, 'Europe/Vienna', true)}
            </Tag>
          );
        },
      },
      {
        Header: t('tasks:data_table.task_type'),
        accessor: 'type',
        // @ts-ignore
        Cell: (cellProps: { row: any }) => {
          const { row } = cellProps;

          // @ts-ignore
          let type = t(TASK_TYPE[row.original.type]);
          return <Tag variant={'primary'}>{type}</Tag>;
        },
        shouldFilter: true,
        // @ts-ignore
        Filter: ({ column }) =>
          SelectColumnFilter({
            filterValue: type,
            column,
            dropdownOptions: createDropdownPlainOptions({
              records: allTaskTypes,
              valueColumn: 'value',
              labelColumn: 'label',
            }),
            onChange: setType,
          }),
      },
      {
        Header: t('tasks:task_priority.label'),
        accessor: 'priority',
        // @ts-ignore
        Cell: ({ row }) => {
          const priorityValue = row?.values?.priority;
          const priorityObj = PRIORITY_MAP.find((item) => item.value === priorityValue);
          if (!priorityObj) return null;
          return (
            <Tag variant="outline" display="flex" alignItems="center">
              <TagLeftIcon boxSize="18px" as={() => priorityObj.icon} />
              <TagLabel ml={1}>{priorityObj.text}</TagLabel>
            </Tag>
          );
        },
        shouldFilter: true,
        Filter: () =>
          MultiSelectDropDown(
            priority,
            (value) => setPriority(value.map((v) => Number(v))),
            'tasks',
            PRIORITY_MAP.reduce(
              (acc, item) => {
                acc[item.value] = item.text;
                return acc;
              },
              {} as Record<number, string>,
            ),
            undefined,
            t('tasks:task_priority.label'),
            true,
          ),
      },
      {
        Header: t('tasks:data_table.subject_id'),
        accessor: 'subject_id',
        Cell: (cellProps: any) => {
          const { row } = cellProps;
          return (
            <Link
              // @ts-ignore
              to={`/app/op/${PagePathMapping[row.original.type]}/${row.original.subject_id}`}
              data-test-id={'edit-' + row.index}
            >
              {row.original?.subject?.user_readable_id}
            </Link>
          );
        },
      },
      {
        Header: t('tasks:data_table.created_at'),
        accessor: 'created_at',
        Cell: (cellProps: any) => {
          const date = cellProps?.row?.original?.created_at;
          return <Text>{dateTimeTransformer(date)}</Text>;
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tasksStatusCodes, searchString, status, type, tasksQuery?.data?.data, priority],
  );

  return (
    <ContentCard icon={<TaskIcon boxSize={10} />} minH="fit-content" header={t('tasks:task_page.user_title')} data-test-id="tasks-overview-card">
      <Stack direction={['column', 'row']} w={'100%'}>
        <Stack direction="column" w={'100%'}>
          <DataTable
            resetFilter={() => {
              setSearchString(initialFilters?.searchString);
              setPage(1);
              setStatus(initialFilters?.status);
              setType(initialFilters?.type);
              setItemsPerPage(initialFilters?.itemsPerPage);
              setPriority(initialFilters?.priority);
            }}
            data-test-id="tasks-table"
            data={tasksQuery?.data?.data || []}
            globalFilter={searchString}
            // @ts-ignore
            setGlobalFilter={setSearchString}
            autoFocus={autoFocus}
            globalFilterPlaceholder={t('tasks:filter.search.id')}
            // @ts-ignore
            hasFilters={true}
            filtersSelected={filtersSelected}
            hasSearchInput={true}
            currentPage={currentPage}
            perPage={itemsPerPage}
            total={tasksQuery.data?.last_page || 1}
            columns={columns}
            setPage={setPage}
            setPerPage={setItemsPerPage}
            loading={tasksQuery.isLoading || usersQuery.isLoading}
            isMoreThenOnePage={!initialLoad || Boolean(tasksQuery.data?.next_page_url) || currentPage > 0}
          />
        </Stack>
      </Stack>
    </ContentCard>
  );
};

export default MyTasksOverviewCard;
